import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  signIn as cognitoSignIn,
  signOut as cognitoSignOut,
  getCurrentSession,
  getUserRole,
  getUserAttributes,
  completeNewPassword,
  ROLES,
  forgotPassword,
  resetPassword,
} from '../services/cognitoService';
import { linkMarketplaceOnboarding } from '../services/marketplaceOnboardingService';

const AuthContext = createContext(null);

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const syncMarketplaceOnboarding = useCallback(async (attrs) => {
    try {
      await linkMarketplaceOnboarding({
        username: attrs.email || attrs.sub,
        email: attrs.email,
        sub: attrs.sub,
      });
    } catch (marketplaceError) {
      console.warn('Marketplace link failed:', marketplaceError);
      setError((currentError) => currentError || marketplaceError.message || 'Marketplace account linking failed.');
    }
  }, []);

  const loadUser = useCallback(async () => {
    try {
      const session = await getCurrentSession();
      const userRole = getUserRole(session);
      const attrs = await getUserAttributes();
      setUser({ username: attrs.email || attrs.sub, email: attrs.email, attributes: attrs, session });
      setRole(userRole);
      await syncMarketplaceOnboarding(attrs);
    } catch {
      setUser(null);
      setRole(null);
    } finally {
      setLoading(false);
    }
  }, [syncMarketplaceOnboarding]);

  useEffect(() => { loadUser(); }, [loadUser]);

  const signIn = async (username, password) => {
    setError(null);
    const result = await cognitoSignIn(username, password);
    if (result.newPasswordRequired) {
      return { newPasswordRequired: true, cognitoUser: result.user, userAttributes: result.userAttributes };
    }
    await loadUser();
    return { success: true };
  };

  const handleNewPassword = async (cognitoUser, newPassword, userAttributes) => {
    setError(null);
    await completeNewPassword(cognitoUser, newPassword, userAttributes);
    await loadUser();
    return { success: true };
  };

  const signOut = async () => {
    await cognitoSignOut();
    setUser(null);
    setRole(null);
  };

  const forgotPasswordHandler = async (username) => {
    setError(null);
    return forgotPassword(username);
  };

  const resetPasswordHandler = async (username, code, newPassword) => {
    setError(null);
    return resetPassword(username, code, newPassword);
  };

  const getAccessToken = async () => {
    const session = await getCurrentSession();
    return session.getIdToken().getJwtToken();
  };

  const value = {
    user, role, loading, error,
    signIn, signOut, handleNewPassword,
    forgotPassword: forgotPasswordHandler,
    resetPassword: resetPasswordHandler,
    getAccessToken,
    isAdmin: role === ROLES.ADMIN,
    isUser: !!user,
    ROLES,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
